import { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { TooltipContentProps } from "recharts";
import { useTranslation } from "react-i18next";
import {
  ASPECT_RATIO_OTHER,
  ASPECT_RATIO_VALUES,
  type AspectRatioValue,
  type CompetitorAspectRatioMix,
  type DateWindow,
} from "@nexus/contracts";
import { ToggleGroup, ToggleGroupItem } from "@nexus/ui";
import { formatBrandName, formatCountValue } from "../../lib/format";
import { computeChartTitle } from "../../lib/periodBuckets";
import { CHART_AXIS_TICK, CHART_GRID_STROKE } from "../../lib/kpi-chart-style";
import {
  buildAspectRatioMixBars,
  type AspectRatioMixDisplay,
  type AspectRatioMixEntity,
} from "../../lib/competitor-aspect-ratio-mix";
import type { CompetitorKpiCardsState } from "../CompetitorKpiCards/CompetitorKpiCards";
import { KpiChartCard } from "../KpiChartCard/KpiChartCard";
import { KpiChartCardMessage } from "../KpiChartCard/KpiChartCardMessage";
import { KpiChartCardSkeleton } from "../KpiChartCard/KpiChartCardSkeleton";

/** Categorical series with no named KPI take the chart palette in order (DESIGN.md). */
const RATIO_COLORS: Record<AspectRatioValue, string> = {
  "16:9": "var(--chart-1)",
  "9:16": "var(--chart-2)",
  "1:1": "var(--chart-3)",
  "4:5": "var(--chart-4)",
  Other: "var(--chart-5)",
};

const CHART_HEIGHT = 180;
const BAR_SIZE = 28;
const Y_AXIS_WIDTH = 120;
const SHARE_DOMAIN: [number, number] = [0, 100];
const SHARE_TICKS = [0, 25, 50, 75, 100];

interface CompetitorAspectRatioChartProps {
  state: CompetitorKpiCardsState;
  mix?: CompetitorAspectRatioMix;
  window: DateWindow;
}

export function CompetitorAspectRatioChart({ state, mix, window }: CompetitorAspectRatioChartProps) {
  const { t } = useTranslation();
  const [display, setDisplay] = useState<AspectRatioMixDisplay>("share");

  if (state === "blocked") {
    return (
      <KpiChartCardMessage>
        {t("dashboard.aspectRatioChart.selectionRequired", "Select your brand and main competitor to see data.")}
      </KpiChartCardMessage>
    );
  }
  if (state === "error") {
    return <KpiChartCardMessage tone="error">{t("dashboard.aspectRatioChart.error")}</KpiChartCardMessage>;
  }
  if (state === "loading" || (state === "ready" && !mix)) return <KpiChartCardSkeleton />;
  if (state === "empty" || !mix) {
    return <KpiChartCardMessage>{t("dashboard.aspectRatioChart.empty")}</KpiChartCardMessage>;
  }

  const entityNames: Record<AspectRatioMixEntity, string> = {
    you: formatBrandName(mix.brandLabels.you || t("dashboard.competitorChart.you")),
    mainCompetitor: formatBrandName(mix.brandLabels.mainCompetitor || t("dashboard.competitorChart.mainCompetitor")),
    benchmark: t("dashboard.competitorChart.benchmark"),
  };
  const ratioName = (ratio: AspectRatioValue) =>
    ratio === ASPECT_RATIO_OTHER ? t("dashboard.aspectRatioChart.other") : ratio;
  const isShare = display === "share";
  const bars = buildAspectRatioMixBars(mix, display);

  return (
    <KpiChartCard
      title={t("dashboard.aspectRatioChart.title")}
      subtitle={t("dashboard.aspectRatioChart.subtitle", { period: computeChartTitle(window) })}
      legend={
        <div className="flex flex-wrap items-center gap-4">
          <RatioLegend ratioName={ratioName} />
          <ToggleGroup
            type="single"
            value={display}
            onValueChange={(value) => {
              if (value) setDisplay(value as AspectRatioMixDisplay);
            }}
            variant="pill"
            aria-label={t("dashboard.aspectRatioChart.displayLabel")}
            data-testid="competitor-aspect-ratio-toggle"
          >
            <ToggleGroupItem value="share" size="sm">
              {t("dashboard.aspectRatioChart.share")}
            </ToggleGroupItem>
            <ToggleGroupItem value="count" size="sm">
              {t("dashboard.aspectRatioChart.count")}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      }
    >
      <div data-testid="competitor-aspect-ratio-chart">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={bars} layout="vertical" margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} horizontal={false} />
            <XAxis
              type="number"
              domain={isShare ? SHARE_DOMAIN : undefined}
              // Stacked float shares can sum to 100.00000000000001; without these
              // recharts widens the domain and picks uneven ticks with that tail.
              ticks={isShare ? SHARE_TICKS : undefined}
              allowDataOverflow={isShare}
              tick={CHART_AXIS_TICK}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => (isShare ? `${value}%` : formatCountValue(value))}
            />
            <YAxis
              type="category"
              dataKey="entity"
              tick={CHART_AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={Y_AXIS_WIDTH}
              tickFormatter={(entity: AspectRatioMixEntity) => entityNames[entity]}
            />
            <Tooltip
              cursor={{ fill: "var(--muted)", opacity: 0.4 }}
              content={(props) => (
                <MixTooltip
                  {...(props as TooltipContentProps<number, string>)}
                  mix={mix}
                  entityNames={entityNames}
                  ratioName={ratioName}
                />
              )}
            />
            {ASPECT_RATIO_VALUES.map((ratio) => (
              <Bar
                key={ratio}
                dataKey={ratio}
                stackId="mix"
                fill={RATIO_COLORS[ratio]}
                barSize={BAR_SIZE}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </KpiChartCard>
  );
}

function RatioLegend({ ratioName }: { ratioName: (ratio: AspectRatioValue) => string }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {ASPECT_RATIO_VALUES.map((ratio) => (
        <span key={ratio} className="flex items-center gap-1.5">
          <span className="size-2.5 shrink-0 rounded-[2px]" style={{ background: RATIO_COLORS[ratio] }} aria-hidden />
          <span className="text-[12px] text-foreground/80">{ratioName(ratio)}</span>
        </span>
      ))}
    </div>
  );
}

/** Shows count and share together, whichever view is active. */
function MixTooltip({
  active,
  label,
  mix,
  entityNames,
  ratioName,
}: TooltipContentProps<number, string> & {
  mix: CompetitorAspectRatioMix;
  entityNames: Record<AspectRatioMixEntity, string>;
  ratioName: (ratio: AspectRatioValue) => string;
}) {
  if (!active || label === undefined) return null;
  const entity = label as AspectRatioMixEntity;
  const shareBar = buildAspectRatioMixBars(mix, "share").find((bar) => bar.entity === entity);
  if (!shareBar) return null;

  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1.5 font-medium text-foreground">{entityNames[entity]}</p>
      {mix.rows.map((row) => (
        <p key={row.ratio} className="tabular-nums text-muted-foreground">
          <span className="font-medium text-foreground">{ratioName(row.ratio)}:</span>{" "}
          {formatCountValue(row[entity])} ({Math.round(shareBar[row.ratio])}%)
        </p>
      ))}
    </div>
  );
}
